import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Phone,
  Users,
  BadgeCheck,
  Sparkles,
} from "lucide-react";
import AnimatedHeading from "../../../animations/AnimatedHeading";

const process = [
  {
    icon: FileText,
    title: "Application Review",
    description:
      "Our recruitment team carefully evaluates your experience, portfolio, and technical expertise to understand how your profile aligns with the role.",
  },
  {
    icon: Phone,
    title: "HR Conversation",
    description:
      "A friendly discussion to understand your career goals, expectations, communication skills, and how well you'll fit into our collaborative culture.",
  },
  {
    icon: Users,
    title: "Technical Assessment",
    description:
      "Meet our technical team to showcase your practical knowledge, problem-solving approach, and ability to work on real-world projects.",
  },
  {
    icon: BadgeCheck,
    title: "Offer & Onboarding",
    description:
      "Successful candidates receive an offer letter followed by a structured onboarding process designed to help them succeed from day one.",
  },
];

const HiringProcess = () => {
  return (
    <section className="theme-bg-primary relative overflow-hidden py-1 font-saira transition-colors duration-300 sm:py-2 lg:py-3">
      {/* Background Glow */}
      <motion.div
        animate={{
          x: [-20, 20, -20],
          y: [-20, 20, -20],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
        }}
        className="pointer-events-none absolute -left-20 top-0 h-64 w-64 rounded-full bg-[color:var(--brand-primary)]/10 blur-[110px] sm:h-80 sm:w-80 sm:blur-[130px]"
      />

      <motion.div
        animate={{
          x: [20, -20, 20],
          y: [20, -20, 20],
        }}
        transition={{
          duration: 17,
          repeat: Infinity,
          ease: "linear",
        }}
        className="pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-[color:var(--brand-primary)]/10 blur-[110px] sm:h-80 sm:w-80 sm:blur-[130px]"
      />

      {/* Container */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{
            opacity: 0,
            y: 35,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
          }}
          className="mb-10 text-center sm:mb-12"
        >
          {/* Section Label */}
          <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand-primary)]/20 bg-[color:var(--brand-primary)]/10 px-4 py-2 sm:px-5">
            <Sparkles
              size={16}
              className="text-[color:var(--brand-primary)]"
            />

            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[color:var(--brand-light)] sm:text-sm">
              HIRING PROCESS
            </span>
          </div>

          {/* Heading */}
          <div>
            <AnimatedHeading
              title="Your Journey To WebBocket"
              highlight="Journey"
            />
          </div>

          {/* Description */}
          <p className="theme-text-muted mx-auto max-w-7xl text-base leading-8 transition-colors duration-300 sm:text-lg sm:leading-9">
            We believe hiring should be transparent, respectful, and
            efficient. Every stage of our recruitment process is thoughtfully
            designed to understand your strengths, showcase your potential,
            and give you a clear insight into our culture, values, and what
            it's like to build a rewarding career at WebBocket.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-6 top-0 hidden h-full w-[2px] bg-gradient-to-b from-[color:var(--brand-primary)] via-[color:var(--brand-light)]/40 to-transparent lg:block" />

          <div className="space-y-5 sm:space-y-6">
            {process.map((item, index) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    y: 30,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.45,
                    delay: index * 0.08,
                  }}
                  className="relative flex items-start gap-4 sm:gap-5 lg:gap-6"
                >
                  {/* Timeline Icon */}
                  <div className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[color:var(--brand-primary)]/30 bg-[color:var(--card-bg-secondary)] text-[color:var(--brand-primary)] shadow-[0_0_20px_rgba(249,115,22,0.15)] transition-all duration-300 sm:h-12 sm:w-12 sm:rounded-2xl">
                    <Icon
                      size={20}
                      className="sm:h-[22px] sm:w-[22px]"
                    />
                  </div>

                  {/* Content Card */}
                  <motion.div
                    whileHover={{
                      y: -5,
                    }}
                    className="theme-card group relative flex-1 overflow-hidden rounded-2xl border border-[color:var(--border-primary)] p-5 transition-all duration-300 hover:border-[color:var(--brand-primary)]/40 hover:bg-[color:var(--bg-tertiary)] sm:p-6"
                  >
                    {/* Hover Glow */}
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.12),transparent_70%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                    {/* Left Accent */}
                    <div className="absolute left-0 top-0 h-full w-[3px] bg-[color:var(--brand-primary)]" />

                    <div className="relative z-10">
                      {/* Step Badge */}
                      <div className="inline-flex rounded-full border border-[color:var(--brand-primary)]/20 bg-[color:var(--brand-primary)]/10 px-3 py-1">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[color:var(--brand-light)]">
                          Step {index + 1}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="theme-text-primary mt-4 text-lg font-semibold leading-7 transition-colors duration-300 sm:text-xl">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="theme-text-muted mt-3 text-[15px] leading-7 transition-colors duration-300 sm:text-base sm:leading-8">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HiringProcess;